import { Box, TextField } from "@mui/material";
import Popover from "@mui/material/Popover";
import type React from "react";
import { useState } from "react";
import { tagColors } from "../utils/sectionColors";
import { useTagColorsStyles } from "../styles/sectionColorsStyles";

interface SwatchProps {
	color: string;
	onSelect: (color: string) => void;
	onClose: () => void;
}

const Swatch = ({ color, onSelect, onClose }: SwatchProps) => {
	const classes = useTagColorsStyles();
	const handleClick = () => {
		onSelect(color);
		onClose();
	};

	return (
		<div
			className={classes.swatch}
			style={{ backgroundColor: color }}
			onClick={handleClick}
		/>
	);
};

interface Props {
	onChange: (color: string) => void;
	color: string;
	label?: string;
}

export const ColorPicker = ({ onChange, color, label }: Props) => {
	const classes = useTagColorsStyles();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;

	return (
		<div>
			<TextField
				aria-describedby={id}
				label={label}
				variant="outlined"
				fullWidth
				margin="dense"
				value={color ?? ""}
				placeholder="No color"
				onClick={handleClick}
				slotProps={{
					inputLabel: { shrink: true },
					htmlInput: { readOnly: true, style: { cursor: "pointer" } },
					input: {
						sx: { cursor: "pointer" },
						startAdornment: (
							<Box
								sx={{
									width: 20,
									height: 20,
									mr: 1,
									flexShrink: 0,
									borderRadius: "4px",
									border: 1,
									borderColor: "divider",
									backgroundColor: color || "transparent",
								}}
							/>
						),
					},
				}}
			/>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
			>
				<div className={classes.popover}>
					{tagColors.map((color) => (
						<Swatch
							key={color}
							color={color}
							onSelect={onChange}
							onClose={handleClose}
						/>
					))}
				</div>
			</Popover>
		</div>
	);
};
